/**
 * Design tab shell. The grouped left nav (DesignNav) lists the Build Plan
 * card, Design Docs, and Screens; the centre panel renders the editor for
 * whatever's selected; the right rail stacks the compact Build
 * card (BuildRailCard — build status + primary action) over the
 * Co-Designer. Workflow state derives from design-status.ts so every surface
 * agrees. Sections use the WYSIWYG editor (TipTap). Self-contained React
 * state — the full Anvil Zustand store port is a later phase.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type {
  BuildUpdateStatus,
  DesignChangeSummary,
  DesignProject,
  DesignReconcileStatus,
  LevelEntry,
} from '@volley/crucible-ipc-types';
import { CrucibleError, type CrucibleClient } from '@volley/crucible-client';
import type { AutoBuildStatus } from '@volley/crucible-ipc-types/build-game-types';
import { Wand2, Rows3, FileText, Frame } from 'lucide-react';
import { useCrucibleClient } from '../../transport/context.js';
import {
  getBuildSnapshotSection,
  getBuildUpdateStatus,
  getReconcileStatus,
  openProject,
  readSection,
  setSectionItemMode,
  writeProjectProfile,
  writeSection,
} from './anvil-api';
import type { DesignProfile } from '@volley/crucible-ipc-types';
import { BuildHandoffDialog } from './BuildHandoffDialog';
import { SectionWysiwyg } from './SectionWysiwyg';
import { SectionItemsEditor } from './SectionItemsEditor';
import { ItemProposalDialog } from './ItemProposalDialog';
import {
  itemModeFor,
  mergeItemsMarkdown,
  appendFreeformItem,
  replyHasItems,
  isGameLoopSection,
  parseItems,
} from './lib/section-items';
import { CoPilotPanel } from './CoPilotPanel';
import { ScreenDetail, useScreens } from './ScreenEditor';
import {
  LevelDetail,
  useLevels,
  DEFAULT_TOPDOWN_THREE_CS,
  DEFAULT_DATA_THREE_CS,
} from './LevelEditor';
import { ThreeCsEditor, useThreeCs } from './ThreeCsEditor';
import {
  ProgressionEditor,
  useProgression,
  progressionOrder,
  progressionWorldGroups,
} from './ProgressionEditor';
import { levelSelectScreenFromProgression } from './lib/level-select-screen';
import {
  generateLevelFromDesign,
  deriveThreeCsArchetype,
  extractLevelManifest,
} from './level-generate';
import { NewLevelWizard } from './NewLevelWizard';
import { DesignSearchPalette } from './DesignSearchPalette';
import { buildDesignIndex, resolveMention, tagCounts, type ConceptRef } from './lib/design-index';
import {
  buildUsageIndex,
  screenLevelRefs,
  levelIsReferenced,
  renameRefInLevelData,
  type LevelForUsage,
  type RenamableLevelData,
} from './lib/usage-index';
import {
  loadOrDerivePrevisSkin,
  loadPrevisImages,
  type PrevisSkin,
  type PrevisImages,
} from './previs-skin';
import { inlineSuggestions } from './lib/suggest';
import { extractTags } from './lib/tags';
import { scanLevelData } from './level-validate';
import { threeCsForKind, isKindChoice } from './level-defaults';
import { generateScreensFromDesign } from './screen-generate';
import { SyncDesignDialog, type SyncDesignDialogMode } from './SyncDesignDialog';
import { ReconcileDialog } from './ReconcileDialog';
import { WizardDialog } from './WizardDialog';
import { LensPanel, type LensSelection } from './LensPanel';
import { DesignNav } from './DesignNav';
import { GameProfile } from './GameProfile';
import { Tip } from './Tip';
import { BuildRailCard, type DesignBuildActivity } from './BuildRailCard';
import {
  deriveBuildCardModel,
  deriveBuildPlanChip,
  deriveNavFooterLine,
  deriveScreensGenerateState,
  designChangesPending,
  type DesignStatusInputs,
} from './design-status';
import { isScreensStale, markDocsChanged, markScreensGenerated } from './screens-stale';
import { PlanWorkspace, type PlanWorkspaceProps } from '../../components/PlanWorkspace';
import type { DesignBuildPlanSeed } from '@volley/crucible-ipc-types';

/** Placeholder marker written by the scaffolder into a fresh section body. */
const PLACEHOLDER_MARKER = '_(empty — fill in';
const AUTHORSHIP_RETRY_DELAY_MS = 250;

export interface DesignWorkspaceProps {
  gameId: string;
  /** Existing build-game plan surface embedded inside Design. */
  buildPlan?: Omit<PlanWorkspaceProps, 'gameId' | 'gameName' | 'onStartBuilding'> & {
    gameName: string;
    focusNonce: number;
    /** Guided review already owns the durable approval boundary, so it must
     *  dispatch directly instead of opening Design's separate export dialog. */
    guidedPlanReview?: boolean;
    onStartBuilding: (seededPlan?: DesignBuildPlanSeed) => Promise<void> | void;
  };
  /** Called when a FULL build is sent ("Send to build"), so the host can
   *  start the build through the existing build-game state machine. */
  onSentToBuild?: (seededPlan?: DesignBuildPlanSeed) => Promise<void> | void;
  /** Called when build UPDATES are accepted ("Send design changes"): the host
   *  switches to the Build surface and auto-sends the edit list to the agent. */
  onSendUpdate?: (message: string) => void;
  /** Live build progress for the right-rail Build card. */
  buildActivity?: DesignBuildActivity | null;
  /** Automatic build-first setup state, when Fast/Just Build owns drafting. */
  autoBuildStatus?: AutoBuildStatus | null;
  /** Consumer north-star flow owns build approval; do not surface prosumer review gates. */
  consumerAutoBuild?: boolean;
  /** Switches the workspace into the Art tab. Passed only when Art is actually
   *  reachable (`flags.artTab && flags.newUi && gameId`) — the rail button
   *  below is gated on this prop's presence, not its own copy of the flags. */
  onGoToArt?: () => void;
  /** Expert level-design tooling gate (design §23 — CRUCIBLE_LEVEL_DESIGN_PRO). When false,
   *  the design-judge, build→design capture, and worlds nav grouping are hidden. The level
   *  model/validation (pace, conveyor, encounters) is un-gated (data-opt-in). */
  levelDesignPro?: boolean;
}

/** What the centre panel is showing. */
type View =
  | { kind: 'section'; filename: string }
  | { kind: 'screen'; screenId: string | null }
  | { kind: 'level'; levelId: string | null }
  | { kind: 'threeCs' }
  | { kind: 'progression' }
  | { kind: 'build-plan' };

type SaveState = 'idle' | 'saving' | 'saved' | 'error';
type ReconcileStatusState = { gameId: string; status: DesignReconcileStatus };
type ReconcileOpenState = { gameId: string; open: boolean };
type DesignAuthorshipState = 'unknown' | 'scaffolded' | 'authored';

function isAuthoredSectionBody(text: string): boolean {
  return text.trim() !== '' && !text.includes(PLACEHOLDER_MARKER);
}

function isMissingSectionError(error: unknown): boolean {
  return error instanceof CrucibleError && error.systemError?.code === 'ENOENT';
}

/**
 * Classifies the design's authorship from the per-section bodies.
 *
 * Args:
 *   bodies: One entry per section — the section text, or `null` when the
 *     read FAILED for a reason other than the file not existing. A read
 *     failure is not evidence the section is empty, so `null` is kept
 *     distinct from `''` (tri-state; #3069).
 *
 * Returns:
 *   'authored' when any successfully read section has authored content;
 *   'unknown' when no authored content was seen but at least one read
 *   failed (the failed section could hold authored content — never
 *   downgrade to 'scaffolded' on partial evidence, which would auto-open
 *   the first-run wizard over an authored design);
 *   'scaffolded' only when every section was read and none is authored.
 */
function deriveDesignAuthorship(bodies: ReadonlyArray<string | null>): DesignAuthorshipState {
  if (bodies.some((b) => b !== null && isAuthoredSectionBody(b))) return 'authored';
  if (bodies.some((b) => b === null)) return 'unknown';
  return 'scaffolded';
}

async function readDesignAuthorship(
  client: CrucibleClient,
  gameId: string,
  sections: DesignProject['sections'],
): Promise<DesignAuthorshipState> {
  const bodies = await Promise.all(
    sections.map((section) =>
      readSection(client, gameId, section.filename).catch((error: unknown): string | null => {
        // A missing section file is definitively empty content; any other
        // failure could hide authored content and must remain unknown (#3069).
        return isMissingSectionError(error) ? '' : null;
      }),
    ),
  );
  return deriveDesignAuthorship(bodies);
}

function getDesignWizardTooltip(
  autoBuildBusy: boolean,
  authorshipState: DesignAuthorshipState,
): string {
  if (autoBuildBusy) return 'Build setup is drafting the design docs';
  if (authorshipState === 'authored') {
    return 'Revise design — propose cross-document edits for review';
  }
  if (authorshipState === 'unknown') {
    return 'Design wizard unavailable until every design section can be read; transient failures are retried once';
  }
  return 'Design wizard — draft all sections from a pitch interview';
}

/**
 * Merges server-reported design diffs with client-local pending section edits.
 *
 * Args:
 *   serverSummary: Diff summary returned from the build snapshot comparison, if available.
 *   localSummary: Section edits saved locally before the server diff observes them.
 *
 * Returns:
 *   A single summary containing both sources, or undefined when neither source exists.
 *
 * Raises:
 *   None.
 */
function mergeDesignChangeSummaries(
  serverSummary: DesignChangeSummary | undefined,
  localSummary: DesignChangeSummary | null,
): DesignChangeSummary | undefined {
  if (!serverSummary) return localSummary ?? undefined;
  if (!localSummary) return serverSummary;

  return {
    sections: {
      added: Array.from(new Set([...serverSummary.sections.added, ...localSummary.sections.added])),
      removed: Array.from(
        new Set([...serverSummary.sections.removed, ...localSummary.sections.removed]),
      ),
      changed: Array.from(
        new Set([...serverSummary.sections.changed, ...localSummary.sections.changed]),
      ),
    },
    screens: {
      added: Array.from(new Set([...serverSummary.screens.added, ...localSummary.screens.added])),
      removed: Array.from(
        new Set([...serverSummary.screens.removed, ...localSummary.screens.removed]),
      ),
      changed: Array.from(
        new Set([...serverSummary.screens.changed, ...localSummary.screens.changed]),
      ),
    },
    graphChanged: serverSummary.graphChanged || localSummary.graphChanged,
  };
}

export function DesignWorkspace({
  gameId,
  buildPlan,
  onSentToBuild,
  onSendUpdate,
  buildActivity,
  autoBuildStatus,
  consumerAutoBuild = false,
  onGoToArt,
  levelDesignPro = false,
}: DesignWorkspaceProps): ReactElement {
  const client = useCrucibleClient();
  const [project, setProject] = useState<DesignProject | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<View>({ kind: 'section', filename: '' });
  const [body, setBody] = useState('');
  // Last-built body for the active section — drives item-level "edited since
  // last build" pips. Undefined when there's no build snapshot yet.
  const [baselineBody, setBaselineBody] = useState<string | undefined>(undefined);
  // A pending Co-Designer "apply to section" — reviewed as a diff before it's
  // written, so a partial reply can never silently overwrite the section.
  const [copilotApply, setCopilotApply] = useState<{
    proposed: string;
    syncInstruction?: string;
    /** True when the reply wasn't item-shaped and was captured as a freeform
     *  fallback item — the dialog explains that rather than showing a normal diff. */
    freeform?: boolean;
  } | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [buildOpen, setBuildOpen] = useState(false);
  const [genScreens, setGenScreens] = useState(false);
  const [genLevels, setGenLevels] = useState(false);
  const [levelGenError, setLevelGenError] = useState<string | null>(null);
  /** Non-fatal outcome ("created with N issues") — amber, never the red error slot. */
  const [levelGenNote, setLevelGenNote] = useState<string | null>(null);
  const [levelGenProgress, setLevelGenProgress] = useState<string | null>(null);
  const [levelWizardOpen, setLevelWizardOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchSeed, setSearchSeed] = useState('');
  const [focusItemId, setFocusItemId] = useState<string | null>(null);

  // ⌘K / Ctrl+K opens the design search palette (design §16). Must live ABOVE the
  // early returns below — hooks order.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchSeed('');
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const [genError, setGenError] = useState<string | null>(null);
  const [syncDialog, setSyncDialog] = useState<{
    mode: SyncDesignDialogMode;
  } | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [authorshipState, setAuthorshipState] = useState<DesignAuthorshipState>('unknown');
  const [selectedLenses, setSelectedLenses] = useState<LensSelection[]>([]);
  const [screensStale, setScreensStale] = useState(false);
  const [buildStatus, setBuildStatus] = useState<BuildUpdateStatus | null>(null);
  const [localSyncSectionIds, setLocalSyncSectionIds] = useState<string[]>([]);
  const [reconcileStatusState, setReconcileStatusState] = useState<ReconcileStatusState | null>(
    null,
  );
  const [reconcileOpenState, setReconcileOpenState] = useState<ReconcileOpenState>({
    gameId,
    open: false,
  });
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wizardAutoTriggered = useRef(false);
  const saveChain = useRef<Promise<void>>(Promise.resolve());
  const saveVersion = useRef(0);
  const currentGameIdRef = useRef(gameId);
  const buildPlanFocusNonce = buildPlan?.focusNonce ?? 0;
  const buildPlanFocusNonceRef = useRef(buildPlanFocusNonce);
  const autoBuildBusy =
    consumerAutoBuild ||
    autoBuildStatus === 'armed' ||
    autoBuildStatus === 'generating-docs' ||
    autoBuildStatus === 'exporting-package' ||
    autoBuildStatus === 'commit-starting';
  currentGameIdRef.current = gameId;
  const reconcileStatus =
    reconcileStatusState?.gameId === gameId ? reconcileStatusState.status : null;
  const reconcileOpen = reconcileOpenState.gameId === gameId ? reconcileOpenState.open : false;

  const setReconcileOpen = useCallback(
    (open: boolean) => {
      setReconcileOpenState({ gameId, open });
    },
    [gameId],
  );

  useEffect(() => {
    buildPlanFocusNonceRef.current = buildPlanFocusNonce;
    if (buildPlanFocusNonce <= 0) return;
    // A Guided handoff owns the next step once it focuses plan review. The
    // first-run Design wizard may already be open (authorship reads can finish
    // before the durable handoff reaches the renderer), so close it as well as
    // suppressing any later scaffold-triggered open.
    wizardAutoTriggered.current = true;
    setWizardOpen(false);
    setView({ kind: 'build-plan' });
  }, [buildPlanFocusNonce]);

  // Drives the per-section edited dots, the Sync design gate, and the Build
  // card's primary action. Refreshed on mount/game-switch and explicitly after
  // section saves, screen mutations, syncs, and sends.
  const refreshBuildStatus = useCallback(() => {
    getBuildUpdateStatus(client, gameId)
      .then(setBuildStatus)
      .catch(() => setBuildStatus(null));
  }, [gameId, client]);

  const refreshReconcileStatus = useCallback(() => {
    const requestedGameId = gameId;
    getReconcileStatus(client, requestedGameId)
      .then((status) => {
        if (currentGameIdRef.current !== requestedGameId) return;
        setReconcileStatusState({ gameId: requestedGameId, status });
      })
      .catch(() => {
        if (currentGameIdRef.current !== requestedGameId) return;
        // A failed refresh cannot prove zero unread activity; keep known reviews visible.
        setReconcileStatusState((current) =>
          current?.gameId === requestedGameId && current.status.newMessageCount > 0
            ? current
            : null,
        );
      });
  }, [gameId, client]);

  // "Generate from design" staleness — per-game, persisted in localStorage.
  useEffect(() => {
    setScreensStale(isScreensStale(gameId));
  }, [gameId]);

  const screensState = useScreens(gameId);
  const levelsState = useLevels(gameId);
  const threeCsState = useThreeCs(gameId);
  const progressionState = useProgression(gameId);
  const activeFilename = view.kind === 'section' && view.filename ? view.filename : null;
  const activeSectionRef = useRef<{ gameId: string; filename: string | null }>({
    gameId,
    filename: activeFilename,
  });

  useEffect(() => {
    activeSectionRef.current = { gameId, filename: activeFilename };
  }, [gameId, activeFilename]);

  const markSectionNeedsSync = useCallback(
    (filename: string) => {
      const sectionId = project?.sections.find((s) => s.filename === filename)?.id;
      if (!sectionId) return;
      setLocalSyncSectionIds((current) =>
        current.includes(sectionId) ? current : [...current, sectionId],
      );
    },
    [project],
  );

  const clearLocalSyncSections = useCallback(() => {
    setLocalSyncSectionIds([]);
  }, []);

  /**
   * Clears local pending sync only for section files reverse reconcile wrote.
   *
   * Args:
   *   filenames: Section filenames reported by the completed reconcile write.
   *
   * Returns:
   *   void.
   *
   * Raises:
   *   None.
   */
  const clearLocalSyncSectionsForFilenames = useCallback(
    (filenames: string[]) => {
      if (filenames.length === 0 || !project) return;

      // Local sync state is section-id based; reconcile reports filenames from disk writes.
      const reconciledIds = new Set(
        project.sections.filter((section) => filenames.includes(section.filename)).map((s) => s.id),
      );
      if (reconciledIds.size === 0) return;

      setLocalSyncSectionIds((current) => current.filter((id) => !reconciledIds.has(id)));
    },
    [project],
  );

  const reloadSectionIfCurrent = useCallback(
    (filename: string) => {
      const requestedGameId = gameId;
      void readSection(client, requestedGameId, filename)
        .then((text) => {
          const current = activeSectionRef.current;
          if (current.gameId !== requestedGameId || current.filename !== filename) return;
          setLoadError(null);
          setBody(text);
        })
        .catch((err: unknown) => {
          const current = activeSectionRef.current;
          if (current.gameId !== requestedGameId || current.filename !== filename) return;
          setLoadError(err instanceof Error ? err.message : String(err));
        });
    },
    [gameId, client],
  );

  // Mutation completion can arrive after the dialog closes, so read the live editor ref.
  const reloadActiveSection = useCallback(() => {
    const current = activeSectionRef.current;
    if (current.gameId === gameId && current.filename) reloadSectionIfCurrent(current.filename);
  }, [gameId, reloadSectionIfCurrent]);

  useEffect(() => {
    let cancelled = false;
    wizardAutoTriggered.current = false;
    saveVersion.current += 1;
    setProject(null);
    setLoadError(null);
    setView({ kind: 'section', filename: '' });
    setBody('');
    setLocalSyncSectionIds([]);
    setAuthorshipState('unknown');
    // Reconcile status is per-game; clear it before the next status request resolves.
    setReconcileStatusState(null);
    setReconcileOpenState({ gameId, open: false });
    openProject(client, gameId)
      .then((p) => {
        if (cancelled) return;
        setProject(p);
        const first = [...p.sections].sort((a, b) => a.order - b.order)[0];
        if (buildPlanFocusNonceRef.current > 0) {
          setView({ kind: 'build-plan' });
        } else if (first) {
          setView({ kind: 'section', filename: first.filename });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [gameId, client]);

  useEffect(() => {
    if (!activeFilename) return;
    let cancelled = false;
    setSaveState('idle');
    setLoadError(null);
    readSection(client, gameId, activeFilename)
      .then((text) => {
        if (cancelled) return;
        setLoadError(null);
        setBody(text);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        if (isMissingSectionError(err)) {
          setBody('');
        } else {
          setLoadError(msg);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [gameId, activeFilename, client]);

  // Fetch the last-built body for the active section so item-mode can show
  // per-item "edited since last build" pips. No snapshot → no baseline (no
  // pips); a section absent from the snapshot resolves '' (all items are new).
  useEffect(() => {
    if (!activeFilename || !buildStatus?.hasSnapshot) {
      setBaselineBody(undefined);
      return;
    }
    let cancelled = false;
    getBuildSnapshotSection(client, gameId, activeFilename)
      .then((b) => {
        if (!cancelled) setBaselineBody(b ?? '');
      })
      .catch(() => {
        if (!cancelled) setBaselineBody(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [gameId, activeFilename, buildStatus?.hasSnapshot, buildStatus?.lastSentAt, client]);

  // Refresh build/reconcile status on mount/game-switch and running-state flips.
  // Mutating actions refresh explicitly; selecting a different section/screen
  // does not change build state and should not re-read the whole design.
  // While a build runs, poll so the Build card's "N doc edits queued" count
  // and "Review Build activity" trigger stay live.
  const buildRunning = buildPlan?.buildRequestPending ?? false;
  useEffect(() => {
    // Refresh on every running-state flip too, so queued edits surface as
    // pending design changes the moment a build finishes.
    refreshBuildStatus();
    refreshReconcileStatus();
    if (!buildRunning) return;
    const timer = setInterval(() => {
      refreshBuildStatus();
      refreshReconcileStatus();
    }, 15_000);
    return () => clearInterval(timer);
  }, [buildRunning, refreshBuildStatus, refreshReconcileStatus]);

  // Louder nudge: when a build FINISHES (run-state edge into 'done') and the
  // Develop chat made changes not yet recorded in the design, open the review
  // automatically. Still human-gated — nothing writes without confirming the
  // diff. Only the building→done edge fires it, so it won't nag on every visit.
  const prevRunKindRef = useRef(buildActivity?.runState?.kind);
  useEffect(() => {
    const prev = prevRunKindRef.current;
    const cur = buildActivity?.runState?.kind;
    prevRunKindRef.current = cur;
    if (cur !== 'done' || prev === undefined || prev === 'done' || autoBuildBusy) return;
    const requestedGameId = gameId;
    getReconcileStatus(client, requestedGameId)
      .then((status) => {
        if (currentGameIdRef.current !== requestedGameId) return;
        if (status.newMessageCount > 0) setReconcileOpen(true);
      })
      .catch(() => {
        /* best-effort; the standing "Sync design from chat" badge still shows */
      });
  }, [buildActivity?.runState?.kind, autoBuildBusy, gameId, setReconcileOpen, client]);

  // Fully empty/scaffolded docs auto-launch the first-run wizard. Any single
  // non-placeholder authored section routes the wand to freeform revision.
  useEffect(() => {
    if (!project) {
      setAuthorshipState('unknown');
      return;
    }
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const readAuthorship = async (retryOnUnknown: boolean): Promise<void> => {
      const nextState = await readDesignAuthorship(client, gameId, project.sections);
      if (cancelled) return;
      setAuthorshipState(nextState);
      if (nextState === 'unknown' && retryOnUnknown) {
        retryTimer = setTimeout(() => {
          void readAuthorship(false);
        }, AUTHORSHIP_RETRY_DELAY_MS);
      }
    };
    void readAuthorship(true);
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [project, gameId, client]);

  useEffect(() => {
    if (
      consumerAutoBuild ||
      autoBuildBusy ||
      buildPlanFocusNonce > 0 ||
      wizardAutoTriggered.current ||
      authorshipState !== 'scaffolded'
    ) {
      return;
    }
    wizardAutoTriggered.current = true;
    setWizardOpen(true);
  }, [authorshipState, autoBuildBusy, buildPlanFocusNonce, consumerAutoBuild]);

  useEffect(() => {
    if (!consumerAutoBuild) return;
    wizardAutoTriggered.current = true;
    setWizardOpen(false);
    setBuildOpen(false);
  }, [consumerAutoBuild]);

  const persist = useCallback(
    (filename: string, text: string) => {
      setSaveState('saving');
      const version = saveVersion.current + 1;
      saveVersion.current = version;
      const job = saveChain.current
        .catch(() => {})
        .then(() => writeSection(client, gameId, filename, text));
      saveChain.current = job.then(
        () => {},
        () => {},
      );
      job
        .then(() => {
          if (version === saveVersion.current) setSaveState('saved');
          if (isAuthoredSectionBody(text)) {
            setAuthorshipState('authored');
          }
          markDocsChanged(gameId);
          markSectionNeedsSync(filename);
          setScreensStale(isScreensStale(gameId));
          refreshBuildStatus();
        })
        .catch(() => {
          if (version === saveVersion.current) setSaveState('error');
        });
    },
    [gameId, markSectionNeedsSync, refreshBuildStatus, client],
  );

  const onBodyChange = useCallback(
    (text: string) => {
      setBody(text);
      if (!activeFilename) return;
      setSaveState('saving');
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => persist(activeFilename, text), 500);
    },
    [activeFilename, persist],
  );

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  // Known vocabulary for autocomplete (TagsField + inline #/@ in bodies):
  // tags most-used-first and concept names, rebuilt when the design changes.
  // MUST stay above the early returns below (hooks order).
  const [known, setKnown] = useState<{ tags: string[]; names: string[]; itemNames: string[] }>({
    tags: [],
    names: [],
    itemNames: [],
  });
  useEffect(() => {
    if (!project) return;
    let cancelled = false;
    buildDesignIndex({
      project,
      screens: screensState.screens,
      levels: levelsState.levels,
      readSectionText: (f) => readSection(client, gameId, f),
    })
      .then((idx) => {
        if (cancelled) return;
        setKnown({
          tags: tagCounts(idx).map((t) => t.tag),
          names: idx.concepts.map((c) => c.name),
          itemNames: idx.concepts.filter((c) => c.type === 'item').map((c) => c.name),
        });
      })
      .catch(() => {
        /* best-effort — autocomplete just stays empty */
      });
    return () => {
      cancelled = true;
    };
  }, [project, gameId, screensState.screens, levelsState.levels]);
  // Tags typed in the open section suggest immediately (the index only sees
  // saved state); merged ahead of the index tags.
  const liveTags = useMemo(() => {
    const s = project?.sections.find((x) => x.filename === activeFilename);
    if (!s || !itemModeFor(s)) return [];
    const out: string[] = [];
    for (const item of parseItems(body).items) {
      out.push(...extractTags(item.body).tags);
      for (const child of item.children) out.push(...extractTags(child.body).tags);
    }
    return out;
  }, [project, activeFilename, body]);
  const tagVocabulary = useMemo(() => [...liveTags, ...known.tags], [liveTags, known.tags]);
  /** Design-item names — the ref-integrity palette levels validate against (design §18). */
  const contentPalette = useMemo(() => new Set(known.itemNames), [known.itemNames]);
  /** Roster health: re-validate every level against the CURRENT 3Cs + Content palette so a
   *  feel-contract tweak or a Content rename surfaces its blast radius in the nav without
   *  opening each level (design §18/§19). Pure + local; recomputes on the inputs only. */
  const levelHealth = useMemo(() => {
    let errorLevels = 0;
    let orphanLevels = 0;
    // Flat play position drives the pace ramp (a level's effective speed), so validate each
    // level at the speed it actually runs at — not the level-1 base.
    const playIndex = new Map(
      progressionState.progression.entries.map((e, i) => [e.levelId, i] as const),
    );
    for (const l of levelsState.levels) {
      const cs = isKindChoice(l.kind)
        ? threeCsForKind(l.kind, threeCsState.profile)
        : threeCsState.profile;
      const scan = scanLevelData(l.kind, l.data, cs, contentPalette, playIndex.get(l.id), l.tags);
      if (scan.errors > 0) errorLevels += 1;
      if (scan.orphanRefs > 0) orphanLevels += 1;
    }
    return { errorLevels, orphanLevels };
  }, [levelsState.levels, threeCsState.profile, contentPalette, progressionState.progression]);
  const getSuggestions = useCallback(
    (kind: 'tag' | 'mention', query: string) =>
      inlineSuggestions({ kind, query, tags: tagVocabulary, names: known.names }),
    [tagVocabulary, known.names],
  );

  // Usage safety net (design §21): the DERIVED reference graph — which levels/screens actually
  // use each Content item, biome, and level — powering the "used in N levels" badges and the
  // delete/rename guardrails. Rebuilt from the live levels + progression + screens; never a
  // hand-toggled flag, so it can't drift from the real refs.
  const screenLevelRefsList = useMemo(
    () =>
      screenLevelRefs(
        screensState.screens,
        levelsState.levels.map((l) => l.id),
      ),
    [screensState.screens, levelsState.levels],
  );
  const usageIndex = useMemo(
    () =>
      buildUsageIndex(
        levelsState.levels.map(
          (l): LevelForUsage => ({ id: l.id, name: l.name, data: l.data as LevelForUsage['data'] }),
        ),
        progressionState.progression.entries.map((e) => e.levelId),
        screenLevelRefsList,
      ),
    [levelsState.levels, progressionState.progression, screenLevelRefsList],
  );
  /** Levels referenced by neither the campaign progression nor any screen — "orphaned" chip. */
  const orphanLevelIds = useMemo(
    () =>
      new Set(
        levelsState.levels.filter((l) => !levelIsReferenced(usageIndex, l.id)).map((l) => l.id),
      ),
    [levelsState.levels, usageIndex],
  );
  /**
   * Explicit Apply-and-propagate for a Content-item rename: rewrite every level's `entity.ref` /
   * `vertex.hazard` that matched the old name, writing back only the levels that changed. Items
   * have no stable id (id = name-slug), so a free heading edit can't be auto-detected as a rename;
   * this is the deliberate "propagate to N levels" the editor offers. Returns the number of levels
   * updated.
   */
  const renameContentItem = useCallback(
    async (oldName: string, newName: string): Promise<number> => {
      let changedLevels = 0;
      for (const l of levelsState.levels) {
        const { data, changed } = renameRefInLevelData(
          (l.data ?? {}) as RenamableLevelData,
          oldName,
          newName,
        );
        if (changed > 0) {
          await levelsState.save({ ...l, data });
          changedLevels += 1;
        }
      }
      return changedLevels;
    },
    [levelsState],
  );

  // Previs skin (design §19): load the cached palette, or derive it once from the
  // Art Direction section so the level previs renders in the game's look.
  const [previsSkin, setPrevisSkin] = useState<PrevisSkin | null>(null);
  const [previsImages, setPrevisImages] = useState<PrevisImages | null>(null);
  const previsSkinTried = useRef(false);
  useEffect(() => {
    if (!project || previsSkinTried.current) return;
    previsSkinTried.current = true;
    loadOrDerivePrevisSkin({ client, gameId, project })
      .then(async (skin) => {
        setPrevisSkin(skin);
        // Decode the built game's image layers + any entity-appearance sprites the skin refs.
        const refsImages =
          skin?.images || Object.values(skin?.appearance ?? {}).some((a) => a.sprite);
        if (refsImages) setPrevisImages(await loadPrevisImages(client, gameId, skin!));
      })
      .catch(() => {
        /* best-effort — previs falls back to the schematic defaults */
      });
  }, [project, gameId]);

  // No authored 3Cs → derive the archetype from the design once (structured genre
  // first, LLM judge otherwise), so a StarTropics-like doesn't assume the
  // side-scroll autorunner default. Unsaved until PJ confirms in the 3Cs editor.
  const threeCsDeriveTried = useRef(false);
  const applyDerivedThreeCs = threeCsState.applyDerived;
  useEffect(() => {
    if (!project || !threeCsState.loaded || threeCsState.authored || threeCsDeriveTried.current)
      return;
    threeCsDeriveTried.current = true;
    deriveThreeCsArchetype(client, { gameId, project })
      .then((archetype) => {
        if (archetype === 'topdown-2d') applyDerivedThreeCs(DEFAULT_TOPDOWN_THREE_CS);
        else if (archetype === 'data-table') applyDerivedThreeCs(DEFAULT_DATA_THREE_CS);
        // autorunner-2d confirms the existing default; null keeps it.
      })
      .catch(() => {
        /* best-effort — the default stands; the 3Cs editor can flip it */
      });
  }, [project, gameId, threeCsState.loaded, threeCsState.authored, applyDerivedThreeCs]);

  if (loadError) {
    return (
      <div className="p-6 text-sm text-red-400" data-testid="design-load-error">
        Failed to load design: {loadError}
      </div>
    );
  }
  if (!project) {
    return (
      <div className="p-6 text-sm text-crucible-muted" data-testid="design-loading">
        Loading design…
      </div>
    );
  }

  const sections = [...project.sections].sort((a, b) => a.order - b.order);
  const statusInputs: DesignStatusInputs = {
    hasPlan: (buildPlan?.plan ?? '').trim().length > 0,
    planRevision: buildPlan?.planRevision,
    planHash: buildPlan?.planHash,
    lastBuiltPlanRevision: buildPlan?.lastBuiltPlanRevision,
    lastBuiltPlanHash: buildPlan?.lastBuiltPlanHash,
    buildRunning: buildPlan?.buildRequestPending ?? false,
    hasInterviewAnswers: (buildPlan?.totalCount ?? buildPlan?.answeredCount ?? 0) > 0,
    autoBuildStatus,
    buildStatus,
    reconcileStatus,
  };
  const designPending = designChangesPending(statusInputs);
  const localSyncSections = localSyncSectionIds
    .map((id) => sections.find((s) => s.id === id))
    .filter((section): section is DesignProject['sections'][number] => section !== undefined);
  const serverChangedSectionIds = buildStatus?.changedSectionIds ?? [];
  const localSyncSectionsOnly = localSyncSections.filter(
    (section) => !serverChangedSectionIds.includes(section.id),
  );
  const localSyncChangedSectionIds = localSyncSectionsOnly.map((s) => s.id);
  const navChangedSectionIds = Array.from(
    new Set([...serverChangedSectionIds, ...localSyncSections.map((s) => s.id)]),
  );
  const localSyncSummary: DesignChangeSummary | null =
    localSyncSectionsOnly.length > 0
      ? {
          sections: {
            added: [],
            removed: [],
            changed: localSyncSectionsOnly.map((s) => s.title),
          },
          screens: { added: [], removed: [], changed: [] },
          graphChanged: false,
        }
      : null;
  const syncBuildStatus: BuildUpdateStatus | null = buildStatus
    ? designPending || localSyncSummary
      ? {
          ...buildStatus,
          hasChanges: buildStatus.hasChanges || localSyncSummary !== null,
          changedCount: buildStatus.changedCount + localSyncChangedSectionIds.length,
          summary: mergeDesignChangeSummaries(buildStatus.summary, localSyncSummary),
          changedSectionIds: navChangedSectionIds,
          changedScreenIds: buildStatus.changedScreenIds ?? [],
        }
      : null
    : localSyncSummary
      ? {
          hasSnapshot: false,
          hasChanges: true,
          changedCount: localSyncChangedSectionIds.length,
          summary: localSyncSummary,
          changedSectionIds: localSyncChangedSectionIds,
          changedScreenIds: [],
        }
      : null;
  const displayStatusInputs: DesignStatusInputs =
    syncBuildStatus?.hasSnapshot === true
      ? { ...statusInputs, buildStatus: syncBuildStatus }
      : statusInputs;
  const syncPending = designPending || syncBuildStatus !== null;
  const syncChangedCount = syncBuildStatus?.changedCount ?? 0;
  const activeSection = activeFilename
    ? (sections.find((s) => s.filename === activeFilename) ?? null)
    : null;
  /** The Content section is the one whose refs levels actually place — gate the usage UI to it. */
  const isContentSection = activeSection
    ? activeSection.id.toLowerCase().includes('content')
    : false;
  const updateProfile = (patch: Partial<DesignProfile>): void => {
    setProject((p) => (p ? { ...p, profile: { ...p.profile, ...patch } } : p));
    void writeProjectProfile(client, gameId, patch)
      .then((updated) => setProject(updated))
      .catch(() => {
        /* best-effort; a reload re-syncs from disk */
      });
  };
  const activeItemMode = activeSection ? itemModeFor(activeSection) : false;
  const toggleSectionMode = (): void => {
    if (!activeSection) return;
    const next = !activeItemMode;
    // Optimistic local update; persist the flag to project.json.
    setProject((p) =>
      p
        ? {
            ...p,
            sections: p.sections.map((s) =>
              s.id === activeSection.id ? { ...s, itemMode: next } : s,
            ),
          }
        : p,
    );
    void setSectionItemMode(client, gameId, activeSection.id, next).catch(() => {
      // Roll back on failure.
      setProject((p) =>
        p
          ? {
              ...p,
              sections: p.sections.map((s) =>
                s.id === activeSection.id ? { ...s, itemMode: activeItemMode } : s,
              ),
            }
          : p,
      );
    });
  };
  const activeScreen =
    view.kind === 'screen' && view.screenId
      ? (screensState.screens.find((s) => s.id === view.screenId) ?? null)
      : null;
  const activeLevel =
    view.kind === 'level' && view.levelId
      ? (levelsState.levels.find((l) => l.id === view.levelId) ?? null)
      : null;
  // Co-Designer apply — never write straight over the section. For an item
  // section we merge the reply's item(s) into the existing ones (by name); for
  // a freeform section we replace. Either way the result is reviewed as a diff
  // (copilotApply → ItemProposalDialog) before anything is written.
  const requestCopilotApply = (markdown: string, syncInstruction?: string): void => {
    if (!activeFilename) return;
    // Item sections merge the reply's item(s) in by name. If the reply isn't
    // item-shaped (the model answered in prose), don't let the merge silently
    // no-op — capture it as a labelled freeform item so it's still reviewable
    // and applyable. Freeform sections replace as before.
    if (activeItemMode && !replyHasItems(markdown)) {
      setCopilotApply({
        proposed: appendFreeformItem(body, markdown, 'Co-Designer notes'),
        syncInstruction,
        freeform: true,
      });
      return;
    }
    const proposed = activeItemMode
      ? mergeItemsMarkdown(body, markdown, {
          pinGameLoop: activeSection ? isGameLoopSection(activeSection.id) : false,
        })
      : markdown;
    setCopilotApply({ proposed, syncInstruction });
  };
  const applyToSection = (markdown: string): void => requestCopilotApply(markdown);

  const newScreen = (): void => {
    void screensState.create().then((s) => {
      setView({ kind: 'screen', screenId: s.id });
      refreshBuildStatus();
    });
  };

  /** One navigate-to-concept resolver — the ⌘K palette and (later) doc links share it. */
  const navigateToConcept = (ref: ConceptRef): void => {
    if (ref.type === 'item') {
      setView({ kind: 'section', filename: ref.sectionFilename });
      setFocusItemId(ref.id);
    } else if (ref.type === 'screen') {
      setView({ kind: 'screen', screenId: ref.id });
    } else {
      setView({ kind: 'level', levelId: ref.id });
    }
  };
  const openSearch = (seed: string): void => {
    setSearchSeed(seed);
    setSearchOpen(true);
  };
  /** `#tag` (inline or chip) = theme/category: ALWAYS the tag view — the seeded
   *  ⌘K palette showing everything tagged. Direct navigation is `@Mention`'s job. */
  const openTagSearch = (tag: string): void => openSearch(`#${tag}`);
  /** `@Mention` = entity reference: resolve the name against the design index and
   *  go straight to the concept; ambiguous/missing falls back to a seeded search. */
  const followMention = (name: string): void => {
    if (!project) {
      openSearch(name);
      return;
    }
    buildDesignIndex({
      project,
      screens: screensState.screens,
      levels: levelsState.levels,
      readSectionText: (f) => readSection(client, gameId, f),
    })
      .then((idx) => {
        const hits = resolveMention(idx, name);
        if (hits.length === 1) navigateToConcept(hits[0]);
        else openSearch(name);
      })
      .catch(() => openSearch(name));
  };

  // "+ New level" opens the wizard; blank-canvas hand-authoring lives inside it.
  const newLevel = (): void => setLevelWizardOpen(true);
  const createBlankLevel = (): void => {
    void levelsState.create(threeCsState.profile).then((l) => {
      setView({ kind: 'level', levelId: l.id });
    });
  };

  /** Generate from design: reconcile the design's enumerated level set (a 10-rung
   *  ladder, named stages) against existing entries and create the MISSING ones with
   *  their design names; when the design enumerates nothing, pitch one level as before. */
  const generateLevels = (): void => {
    if (!project) return;
    setGenLevels(true);
    setLevelGenError(null);
    setLevelGenNote(null);
    setLevelGenProgress('Reading the design…');
    void (async () => {
      const missing = await extractLevelManifest(client, {
        gameId,
        project,
        existing: levelsState.levels,
      }).catch(() => []);
      // The Content section is where the design enumerates levels — the origin anchor.
      const contentSectionId = project.sections.find((s) =>
        s.id.toLowerCase().includes('content'),
      )?.id;

      const progressionIn = progressionState.authored ? progressionState.progression : undefined;

      if (missing.length === 0) {
        const res = await generateLevelFromDesign(client, {
          gameId,
          project,
          threeCs: threeCsState.profile,
          save: levelsState.save,
          levels: levelsState.levels,
          progression: progressionIn,
        });
        setView({ kind: 'level', levelId: res.level.id });
        setLevelGenNote(
          res.findings.length > 0
            ? `Generated with ${res.findings.length} issue(s) still unresolved after ${res.attempts} tries — flagged in the viewport; tweak or regenerate.`
            : null,
        );
        return;
      }

      const created: LevelEntry[] = [];
      const issues: string[] = [];
      for (const [i, m] of missing.entries()) {
        setLevelGenProgress(`Generating ${i + 1}/${missing.length} — ${m.name}`);
        const res = await generateLevelFromDesign(client, {
          gameId,
          project,
          threeCs: threeCsState.profile,
          save: levelsState.save,
          brief: m.brief,
          kind: m.kind,
          pitch: { name: m.name, summary: m.summary, mechanics: [], tags: m.tags },
          // Anchor to the design so a later regenerate re-matches by origin (design §18).
          source: contentSectionId
            ? { sectionId: contentSectionId, designName: m.name }
            : undefined,
          levels: [...levelsState.levels, ...created],
          progression: progressionIn,
          slot: { label: m.name, index: i, total: missing.length },
        });
        created.push(res.level);
        if (res.findings.length > 0) issues.push(`${m.name} (${res.findings.length})`);
      }
      // Append the whole set in ONE progression save (no per-level races).
      if (progressionState.authored && created.length > 0) {
        void progressionState.save({
          ...progressionState.progression,
          entries: [
            ...progressionState.progression.entries,
            ...created.map((l) => ({ levelId: l.id })),
          ],
        });
      }
      if (created[0]) setView({ kind: 'level', levelId: created[0].id });
      setLevelGenNote(
        issues.length > 0
          ? `Created ${created.length} design-defined level(s); unresolved issues in: ${issues.join(', ')} — flagged in their viewports.`
          : null,
      );
    })()
      .catch((e: unknown) => setLevelGenError(e instanceof Error ? e.message : String(e)))
      .finally(() => {
        setGenLevels(false);
        setLevelGenProgress(null);
      });
  };

  const generateScreens = (): void => {
    setGenScreens(true);
    setGenError(null);
    generateScreensFromDesign(client, {
      gameId,
      project,
      existing: screensState.screens,
      lensIds: selectedLenses,
      save: screensState.save,
    })
      .then((written) => {
        if (written[0]) setView({ kind: 'screen', screenId: written[0].id });
        markScreensGenerated(gameId);
        setScreensStale(isScreensStale(gameId));
        refreshBuildStatus();
      })
      .catch((e: unknown) => setGenError(e instanceof Error ? e.message : String(e)))
      .finally(() => setGenScreens(false));
  };

  return (
    <div className="flex h-full min-h-0" data-testid="design-workspace">
      {/* Grouped left nav — Build / Design Docs / Screens plus a one-line
          pinned footer. All states derive from statusInputs. */}
      <DesignNav
        hasBuildPlan={buildPlan !== undefined}
        buildPlanActive={view.kind === 'build-plan'}
        chip={deriveBuildPlanChip(statusInputs)}
        footer={deriveNavFooterLine(displayStatusInputs)}
        sections={sections}
        activeFilename={activeFilename}
        changedSectionIds={navChangedSectionIds}
        changedScreenIds={buildStatus?.changedScreenIds ?? []}
        screens={screensState.screens}
        activeScreenId={view.kind === 'screen' ? view.screenId : null}
        generate={deriveScreensGenerateState({ stale: screensStale, busy: genScreens })}
        genError={genError}
        profileSlot={<GameProfile profile={project.profile} onChange={updateProfile} />}
        onSelectBuildPlan={() => setView({ kind: 'build-plan' })}
        onSelectSection={(filename) => setView({ kind: 'section', filename })}
        onSelectScreen={(screenId) => setView({ kind: 'screen', screenId })}
        onNewScreen={newScreen}
        onGenerateScreens={generateScreens}
        levels={levelsState.levels}
        activeLevelId={view.kind === 'level' ? view.levelId : null}
        levelsGenerating={genLevels}
        levelGenError={levelGenError}
        levelGenNote={levelGenNote}
        levelGenProgress={levelGenProgress}
        onSelectLevel={(levelId) => setView({ kind: 'level', levelId })}
        onNewLevel={newLevel}
        onGenerateLevels={generateLevels}
        threeCsAuthored={threeCsState.authored}
        activeThreeCs={view.kind === 'threeCs'}
        onEditThreeCs={() => setView({ kind: 'threeCs' })}
        progressionAuthored={progressionState.authored}
        activeProgression={view.kind === 'progression'}
        levelBadges={Object.fromEntries(
          levelsState.levels
            .map((l) => [l.id, progressionOrder(progressionState.progression, l.id)] as const)
            .filter(([, v]) => v !== null) as [string, string][],
        )}
        worldGroups={
          levelDesignPro
            ? progressionWorldGroups(
                progressionState.progression,
                levelsState.levels.map((l) => l.id),
              )
            : null
        }
        levelHealth={levelHealth}
        orphanLevelIds={orphanLevelIds}
        onEditProgression={() => setView({ kind: 'progression' })}
      />

      {/* Centre panel */}
      <div className="flex min-w-0 flex-1">
        {view.kind === 'build-plan' && buildPlan && (
          <PlanWorkspace
            gameId={gameId}
            gameName={buildPlan.gameName}
            plan={buildPlan.plan}
            answeredCount={buildPlan.answeredCount}
            totalCount={buildPlan.totalCount}
            planRevision={buildPlan.planRevision}
            planHash={buildPlan.planHash}
            planUpdatedAt={buildPlan.planUpdatedAt}
            planSource={buildPlan.planSource}
            surface="design"
            showInterviewAnswers={(buildPlan.totalCount ?? buildPlan.answeredCount ?? 0) > 0}
            lastBuiltPlanRevision={buildPlan.lastBuiltPlanRevision}
            lastBuiltPlanHash={buildPlan.lastBuiltPlanHash}
            buildRequestPending={buildPlan.buildRequestPending}
            lastBuiltAt={buildStatus?.lastSentAt}
            busy={buildPlan.busy}
            draftPlanPending={buildPlan.draftPlanPending || buildOpen}
            onSavePlan={buildPlan.onSavePlan}
            onStartBuilding={() => {
              if (consumerAutoBuild || buildPlan.guidedPlanReview) {
                void Promise.resolve(buildPlan.onStartBuilding()).catch(() => {});
                return;
              }
              // First builds route through the handoff dialog so the export is
              // reviewed and the snapshot commits — that snapshot is what later
              // lights up Sync design / Send design changes. (Same path the old
              // nav-footer "Send to build" button ran.)
              if (!buildStatus?.hasSnapshot) {
                setBuildOpen(true);
                return;
              }
              void Promise.resolve(buildPlan.onStartBuilding()).catch(() => {});
            }}
            onDraftPlan={() => setBuildOpen(true)}
            onRebuildFromPlan={buildPlan.onRebuildFromPlan}
            onBackToChat={buildPlan.onBackToChat}
            onSubmitRefinement={buildPlan.onSubmitRefinement}
            onStartOver={buildPlan.onStartOver}
          />
        )}

        {view.kind === 'section' && (
          <section className="flex min-w-0 flex-1 flex-col">
            <header className="flex items-center justify-between border-b border-crucible-border px-4 py-2">
              <span className="truncate text-sm text-crucible-muted">
                {activeSection?.title ?? 'No section selected'}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-xs text-crucible-muted" data-testid="design-save-state">
                  {saveState === 'saving' && 'Saving…'}
                  {saveState === 'saved' && 'Saved'}
                  {saveState === 'error' && 'Save failed'}
                </span>
                <Tip
                  align="end"
                  label={
                    autoBuildBusy
                      ? 'Build setup is generating the design docs'
                      : syncPending
                        ? `${syncChangedCount} design change${
                            syncChangedCount === 1 ? '' : 's'
                          } pending — sync to propagate ${
                            syncChangedCount === 1 ? 'it' : 'them'
                          } across the rest of the design`
                        : 'Nothing to sync — edit a design doc and this lights up'
                  }
                >
                  <button
                    type="button"
                    onClick={() => setSyncDialog({ mode: { kind: 'pending-sync' } })}
                    disabled={!syncPending || autoBuildBusy}
                    data-testid="design-sync-button"
                    className={`peer rounded border px-2 py-0.5 text-xs disabled:cursor-not-allowed disabled:opacity-40 ${
                      syncPending
                        ? 'border-crucible-accent/60 text-crucible-accent-hover hover:bg-crucible-bg-elevated'
                        : 'border-crucible-border text-crucible-muted'
                    }`}
                  >
                    Sync design
                  </button>
                </Tip>
                <Tip align="end" label={getDesignWizardTooltip(autoBuildBusy, authorshipState)}>
                  <button
                    type="button"
                    onClick={() => {
                      if (autoBuildBusy || authorshipState === 'unknown') return;
                      if (authorshipState === 'authored') {
                        setSyncDialog({ mode: { kind: 'freeform-revision', source: 'wand' } });
                      } else {
                        setWizardOpen(true);
                      }
                    }}
                    disabled={autoBuildBusy || authorshipState === 'unknown'}
                    data-testid="design-wizard-button"
                    className="peer rounded border border-crucible-border p-1 text-crucible-muted hover:bg-crucible-bg-elevated disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Wand2 size={13} aria-hidden="true" />
                  </button>
                </Tip>
                {activeSection && (
                  <Tip
                    align="end"
                    label={
                      activeItemMode
                        ? 'Switch to document view (one freeform editor)'
                        : 'Switch to items view (discrete cards)'
                    }
                  >
                    <button
                      type="button"
                      onClick={toggleSectionMode}
                      data-testid="design-section-mode-toggle"
                      className="peer rounded border border-crucible-border p-1 text-crucible-muted hover:bg-crucible-bg-elevated"
                    >
                      {activeItemMode ? (
                        <FileText size={13} aria-hidden="true" />
                      ) : (
                        <Rows3 size={13} aria-hidden="true" />
                      )}
                    </button>
                  </Tip>
                )}
                <LensPanel selected={selectedLenses} onChange={setSelectedLenses} />
              </div>
            </header>
            <div className="flex min-h-0 flex-1">
              {activeSection ? (
                activeItemMode ? (
                  <SectionItemsEditor
                    value={body}
                    onChange={onBodyChange}
                    gameId={gameId}
                    sectionTitle={activeSection.title}
                    projectName={project?.name ?? null}
                    baselineBody={baselineBody}
                    hostsGameLoop={isGameLoopSection(activeSection.id)}
                    focusItemId={focusItemId}
                    onFocusItemHandled={() => setFocusItemId(null)}
                    onTagClick={openTagSearch}
                    onMentionClick={followMention}
                    tagSuggestions={tagVocabulary}
                    getSuggestions={getSuggestions}
                    tall={activeSection.id === '00-overview'}
                    usageIndex={isContentSection ? usageIndex : undefined}
                    onRenameContentItem={renameContentItem}
                  />
                ) : (
                  <SectionWysiwyg
                    value={body}
                    onChange={onBodyChange}
                    onTagClick={openTagSearch}
                    onMentionClick={followMention}
                    getSuggestions={getSuggestions}
                  />
                )
              ) : (
                <div className="p-4 text-sm text-crucible-muted">Select a section.</div>
              )}
            </div>
          </section>
        )}

        {view.kind === 'screen' &&
          (activeScreen ? (
            <ScreenDetail
              key={activeScreen.id}
              screen={activeScreen}
              tagSuggestions={tagVocabulary}
              onChange={(s) => void screensState.save(s).then(refreshBuildStatus)}
              onDelete={() => {
                const id = activeScreen.id;
                void screensState
                  .remove(id)
                  .then(() => setView({ kind: 'screen', screenId: null }))
                  .then(refreshBuildStatus);
              }}
            />
          ) : (
            <div className="flex-1 p-4 text-sm text-crucible-muted">Select or create a screen.</div>
          ))}

        {view.kind === 'level' &&
          (activeLevel ? (
            <LevelDetail
              key={activeLevel.id}
              gameId={gameId}
              level={activeLevel}
              judgeEnabled={levelDesignPro}
              threeCs={threeCsState.profile}
              project={project}
              allLevels={levelsState.levels}
              usageIndex={usageIndex}
              progression={progressionState.authored ? progressionState.progression : undefined}
              slot={(() => {
                const entries = progressionState.progression.entries;
                const idx = entries.findIndex((e) => e.levelId === activeLevel.id);
                return idx === -1
                  ? undefined
                  : {
                      label: entries[idx].label ?? String(idx + 1),
                      index: idx,
                      total: entries.length,
                    };
              })()}
              onChange={(l) => void levelsState.save(l)}
              onDelete={() => {
                const id = activeLevel.id;
                const slotIdx = progressionState.progression.entries.findIndex(
                  (e) => e.levelId === id,
                );
                const inProgression = slotIdx !== -1;
                // Deleting a level is the tab's most destructive click — confirm, and
                // strip its progression slot so nothing dangles (eval 2026-07-10).
                const detail = inProgression
                  ? ` It is level ${slotIdx + 1} in your progression — that slot will be removed too.`
                  : '';
                if (!window.confirm(`Delete "${activeLevel.name}"? This can't be undone.${detail}`))
                  return;
                void levelsState
                  .remove(id)
                  .then(() => {
                    if (inProgression && progressionState.authored) {
                      return progressionState.save({
                        ...progressionState.progression,
                        entries: progressionState.progression.entries.filter(
                          (e) => e.levelId !== id,
                        ),
                      });
                    }
                    return undefined;
                  })
                  .then(() => setView({ kind: 'level', levelId: null }));
              }}
              onTagClick={openTagSearch}
              tagSuggestions={tagVocabulary}
              palette={contentPalette}
              previsSkin={previsSkin}
              previsImages={previsImages}
              onPrevisSkinChange={setPrevisSkin}
            />
          ) : (
            <div className="flex-1 p-4 text-sm text-crucible-muted">Select or create a level.</div>
          ))}

        {view.kind === 'threeCs' && (
          <ThreeCsEditor
            profile={threeCsState.profile}
            authored={threeCsState.authored}
            derived={threeCsState.derived}
            onSave={(p) => void threeCsState.save(p)}
          />
        )}

        {view.kind === 'progression' && (
          <ProgressionEditor
            progression={progressionState.progression}
            authored={progressionState.authored}
            levels={levelsState.levels}
            onSave={(p) => void progressionState.save(p)}
            onOpenLevel={(levelId) => setView({ kind: 'level', levelId })}
            onGenerateLevelSelect={() => {
              const existing = screensState.screens.find(
                (s) => s.id === 'screen_level_select' || s.name === 'Level Select',
              );
              const screen = levelSelectScreenFromProgression(
                levelsState.levels,
                progressionState.progression,
                existing,
              );
              void screensState
                .save(screen)
                .then((saved) => setView({ kind: 'screen', screenId: saved.id }));
            }}
          />
        )}
      </div>

      {project && (
        <DesignSearchPalette
          open={searchOpen}
          seed={searchSeed}
          onOpenChange={setSearchOpen}
          gameId={gameId}
          project={project}
          screens={screensState.screens}
          levels={levelsState.levels}
          onNavigate={navigateToConcept}
        />
      )}

      <NewLevelWizard
        open={levelWizardOpen}
        onOpenChange={setLevelWizardOpen}
        gameId={gameId}
        project={project}
        threeCs={threeCsState.profile}
        saveLevel={levelsState.save}
        levels={levelsState.levels}
        progression={progressionState.authored ? progressionState.progression : undefined}
        slot={
          progressionState.authored
            ? {
                label: String(progressionState.progression.entries.length + 1),
                index: progressionState.progression.entries.length,
                total: progressionState.progression.entries.length + 1,
              }
            : undefined
        }
        onCreated={(l, note) => {
          setView({ kind: 'level', levelId: l.id });
          setLevelGenNote(note);
          // Auto-place the new level at the end of an authored progression.
          if (progressionState.authored) {
            void progressionState.save({
              ...progressionState.progression,
              entries: [...progressionState.progression.entries, { levelId: l.id }],
            });
          }
        }}
        onCreatedMany={(created, note) => {
          if (created[0]) setView({ kind: 'level', levelId: created[0].id });
          setLevelGenNote(note);
          // Append the whole campaign in ONE progression save (no per-level races).
          if (progressionState.authored) {
            void progressionState.save({
              ...progressionState.progression,
              entries: [
                ...progressionState.progression.entries,
                ...created.map((l) => ({ levelId: l.id })),
              ],
            });
          }
        }}
        onStartBlank={createBlankLevel}
        onContentChanged={() => {
          markDocsChanged(gameId);
          setScreensStale(isScreensStale(gameId));
          refreshBuildStatus();
          // The wizard just wrote the Content file on disk. If that section is OPEN,
          // the stale in-memory body would clobber the new mechanics on the next
          // debounced autosave — reload it (confirmed bug, eval 2026-07-10 #4).
          const contentFilename = project?.sections.find((s) =>
            s.id.toLowerCase().includes('content'),
          )?.filename;
          if (contentFilename && activeFilename === contentFilename)
            reloadSectionIfCurrent(contentFilename);
        }}
      />

      <SyncDesignDialog
        gameId={gameId}
        project={project}
        open={syncDialog !== null}
        mode={syncDialog?.mode ?? { kind: 'pending-sync' }}
        buildStatus={syncBuildStatus}
        onOpenChange={(next) => {
          if (!next) setSyncDialog(null);
        }}
        lensIds={selectedLenses}
        onApplied={() => {
          clearLocalSyncSections();
          if (activeFilename) reloadSectionIfCurrent(activeFilename);
          refreshBuildStatus();
        }}
      />

      {copilotApply && (
        <ItemProposalDialog
          open
          title={`Apply to ${activeSection?.title ?? 'section'}`}
          applyLabel={copilotApply.freeform ? 'Append to section' : 'Apply to section'}
          note={
            copilotApply.freeform
              ? "The Co-Designer replied in prose, not editable items — it's captured below as a new note you can reshape into items."
              : undefined
          }
          original={body}
          proposed={copilotApply.proposed}
          onApply={(text) => {
            onBodyChange(text);
            if (copilotApply.syncInstruction) {
              setSyncDialog({
                mode: {
                  kind: 'freeform-revision',
                  initialInstruction: copilotApply.syncInstruction,
                },
              });
            }
          }}
          onOpenChange={(o) => {
            if (!o) setCopilotApply(null);
          }}
        />
      )}

      <WizardDialog
        gameId={gameId}
        project={project}
        open={wizardOpen}
        onOpenChange={(next) => {
          if (next && (autoBuildBusy || consumerAutoBuild)) return;
          setWizardOpen(next);
        }}
        lensIds={selectedLenses}
        hasExistingContent={authorshipState === 'authored'}
        onApplied={() => {
          setAuthorshipState('authored');
          if (activeFilename) reloadSectionIfCurrent(activeFilename);
          refreshBuildStatus();
        }}
      />

      <BuildHandoffDialog
        gameId={gameId}
        open={!consumerAutoBuild && buildOpen}
        onOpenChange={(next) => {
          if (consumerAutoBuild && next) return;
          setBuildOpen(next);
        }}
        onSent={() => {
          clearLocalSyncSections();
          refreshBuildStatus();
        }}
        onStartBuild={(seededPlan) => onSentToBuild?.(seededPlan)}
        onSendUpdate={(message) => onSendUpdate?.(message)}
      />

      <ReconcileDialog
        gameId={gameId}
        open={reconcileOpen}
        onOpenChange={setReconcileOpen}
        levelCaptureEnabled={levelDesignPro}
        isItemSection={(id) => {
          const s = project.sections.find((x) => x.id === id);
          return s ? itemModeFor(s) : itemModeFor({ id });
        }}
        onMutationComplete={({ applied, filenames }) => {
          if (applied) reloadActiveSection();
          if (applied) clearLocalSyncSectionsForFilenames(filenames);
          refreshBuildStatus();
          refreshReconcileStatus();
        }}
      />

      {/* Right rail — compact Build card stacked over the Co-Designer. The
          card owns build status AND its action. */}
      <aside className="flex w-80 shrink-0 flex-col border-l border-crucible-border">
        <BuildRailCard
          model={deriveBuildCardModel(displayStatusInputs)}
          buildActivity={buildActivity}
          onPrimaryAction={(action) => {
            if (action.kind === 'review-build-changes') {
              setReconcileOpen(true);
              return;
            }
            setBuildOpen(true);
          }}
        />
        {onGoToArt && (
          <div className="shrink-0 border-b border-crucible-border p-3">
            <button
              type="button"
              onClick={onGoToArt}
              data-testid="design-go-to-art"
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-crucible-border px-3 py-2 text-xs font-semibold text-crucible-fg hover:bg-crucible-bg-elevated"
            >
              <Frame size={13} aria-hidden="true" />
              Generate art
            </button>
          </div>
        )}
        <CoPilotPanel
          gameId={gameId}
          projectName={project.name}
          projectType={project.projectType}
          sectionTitle={view.kind === 'section' ? (activeSection?.title ?? null) : null}
          sectionDoc={view.kind === 'section' ? (activeSection?.doc ?? null) : null}
          sectionBody={view.kind === 'section' ? body : ''}
          sectionIsItems={view.kind === 'section' ? activeItemMode : false}
          project={project}
          lensIds={selectedLenses}
          onApplyToSection={view.kind === 'section' && activeFilename ? applyToSection : undefined}
          onApplyAndSync={
            view.kind === 'section' && activeFilename
              ? (markdown) => requestCopilotApply(markdown, markdown)
              : undefined
          }
          onAdoptRecommendation={(instruction) =>
            setSyncDialog({
              mode: {
                kind: 'freeform-revision',
                initialInstruction: instruction,
                source: 'review-recommendation',
              },
            })
          }
          className="min-h-0 flex-1"
        />
      </aside>
    </div>
  );
}
